"use client";
import * as NavigationMenuPrimitive from "@radix-ui/react-navigation-menu";
import { cva } from "class-variance-authority";
import { ChevronDown } from "lucide-react";
import * as React from "react";

import { useFasterUrlUpdate } from "@/app/hooks/useFastUrlUpdate";
import { cn } from "@/lib/utils";
import Link from "next/link";
import { usePathname } from "next/navigation";

const NavigationMenuContext = React.createContext(true)


const NavigationMenu = React.forwardRef<
  React.ComponentRef<typeof NavigationMenuPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Root>
>(({ className, children, style, ...props }, ref) => {
  const [forceMount, setForceMount] = React.useState(true);

  return <NavigationMenuContext.Provider value={ forceMount } >
    <NavigationMenuPrimitive.Root
      onValueChange={ () => setForceMount(false) }
      delayDuration={ 1000 }
      skipDelayDuration={ 500 }
      ref={ ref }
      dir="rtl"
      className={ cn(
        "relative z-10 flex max-w-max mx-auto items-center justify-center",
        className
      ) }
      { ...props }
    >
      { children }
      <NavigationMenuViewport className={ cn(className?.includes("lg:") && "lg:top-auto lg:start-full lg:ms-1.5", "z-10") } style={ style } />
    </NavigationMenuPrimitive.Root>
  </NavigationMenuContext.Provider>
})
NavigationMenu.displayName = NavigationMenuPrimitive.Root.displayName

const NavigationMenuList = React.forwardRef<
  React.ComponentRef<typeof NavigationMenuPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.List>
>(({ className, ...props }, ref) => (
  <NavigationMenuPrimitive.List
    ref={ ref }
    className={ cn(
      "flex flex-1 list-none gap-1 justify-center items-stretch",
      className
    ) }
    { ...props }
  />
))
NavigationMenuList.displayName = NavigationMenuPrimitive.List.displayName

const NavigationMenuItem = NavigationMenuPrimitive.Item

export const menuClass = `group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-none disabled:pointer-events-none disabled:opacity-50
  bg-nfPurple-950 hover:bg-nfPurple-700 hover:text-nfPurple-100 data-[active]:bg-nfBlue-800/50 data-[state=open]:bg-nfBlue-800/50
  focus:text-nfPurple-100 focus:bg-nfPurple-800`

const navigationMenuTriggerStyle = cva(
  menuClass
)

const NavigationMenuTrigger = React.forwardRef<
  React.ComponentRef<typeof NavigationMenuPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Trigger>
>(({ className, content, children, ...props }, ref) => (
  <NavigationMenuPrimitive.Trigger
    ref={ ref }
    className={ cn(navigationMenuTriggerStyle(), "group", className) }
    { ...props }
  >
    <ChevronDown
      className={ cn("relative top-[1px] ml-1 h-3 w-3 transition duration-200 scale-150 group-data-[state=open]:rotate-90", content) }
      aria-hidden="true"
    />
    { children }
  </NavigationMenuPrimitive.Trigger>
))
NavigationMenuTrigger.displayName = NavigationMenuPrimitive.Trigger.displayName

const NavigationMenuContent = React.forwardRef<
  React.ComponentRef<typeof NavigationMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Content>
>(({ className, ...props }, ref) => {
  const forceMount = React.useContext(NavigationMenuContext);

  return <NavigationMenuPrimitive.Content
    { ...(forceMount && { forceMount }) }
    ref={ ref }
    className={ cn(
      "data-[state=closed]:hidden p-2 left-0 top-0 w-full data-[motion^=from-]:animate-in data-[motion^=to-]:animate-out data-[motion^=from-]:fade-in data-[motion^=to-]:fade-out data-[motion=from-end]:slide-in-from-right-52 data-[motion=from-start]:slide-in-from-left-52 data-[motion=to-end]:slide-out-to-right-52 data-[motion=to-start]:slide-out-to-left-52 md:absolute md:w-auto",
      className
    ) }
    { ...props }
  />
})
NavigationMenuContent.displayName = NavigationMenuPrimitive.Content.displayName


const NavigationMenuLink = (props: {
  children: React.ReactNode, className?: string, activeClass?: string, useMenuClass?: boolean, href: string
   /** changes url faster NOT FOR LAYOUT LINKS**/fasterUrlUpdate?: boolean, [prop: string]: any
}) => {
  const pathname = usePathname();
  const className = cn(props.useMenuClass && menuClass, props.className, pathname == (props.href?.split("?")[0]) && props.activeClass)
  const linkRef = React.useRef<HTMLAnchorElement>(null)

  const newProps = { ...props, children: null, className };
  delete newProps.activeClass;
  delete newProps.useMenuClass;
  delete newProps.fasterUrlUpdate;

  const { setFastUrl } = useFasterUrlUpdate()

  React.useEffect(() => {
    const ref = linkRef.current;
    //if ref doesn't setup yet ignore
    if (!ref)
      return;

    function clickHandler(e: MouseEvent) {
      // Ignore new tab / modified clicks
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;

      //if {fasterUrlUpdate} & has href - update url before actual navigation start 
      if (props.fasterUrlUpdate && props.href) {
        if (/^(https?|mailto|tel):/.test(props.href))
          return console.warn("Attempt using {fasterUrlUpdate} for external url", props.href, "canceled..")
        let resolvedHref = new URL(props.href, window.location.href).pathname;

        //update context to current url
        setFastUrl(resolvedHref)
      }
    }

    //listen to event without overriding link logic
    ref.addEventListener("click", clickHandler)
    return () => {
      //cleanup
      ref.removeEventListener("click", clickHandler)
    }

  }, [linkRef, props.fasterUrlUpdate, props.href, setFastUrl])


  return <NavigationMenuPrimitive.Link ref={ linkRef } asChild>
    <Link { ...newProps }>
      { props.children }
    </Link>
  </NavigationMenuPrimitive.Link>
}

const NavigationMenuViewport = React.forwardRef<
  React.ComponentRef<typeof NavigationMenuPrimitive.Viewport>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Viewport>
>(({ className, style, ...props }, ref) => (
  <div className={ cn("absolute top-full flex justify-center", className) } style={ style }>
    <NavigationMenuPrimitive.Viewport
      className={ cn(
        "origin-top-center relative mt-1.5 h-[var(--radix-navigation-menu-viewport-height)] w-full overflow-hidden rounded-md border shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-90 md:min-w-[var(--radix-navigation-menu-viewport-width)] border-nfPurple-400 bg-nfPurple-800/70 text-nfBlue-100 backdrop-blur-md") }
      ref={ ref }
      { ...props }
    />
  </div>
))
NavigationMenuViewport.displayName =
  NavigationMenuPrimitive.Viewport.displayName

const NavigationMenuIndicator = React.forwardRef<
  React.ComponentRef<typeof NavigationMenuPrimitive.Indicator>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Indicator>
>(({ className, ...props }, ref) => (
  <NavigationMenuPrimitive.Indicator
    ref={ ref }
    className={ cn(
      "top-full z-[1] flex h-1.5 items-end justify-center overflow-hidden data-[state=visible]:animate-in data-[state=hidden]:animate-out data-[state=hidden]:fade-out data-[state=visible]:fade-in",
      className
    ) }
    { ...props }
  >
    <div className="relative top-[60%] h-2 w-2 rotate-45 rounded-tl-sm bg-slate-200 shadow-md dark:bg-slate-800" />
  </NavigationMenuPrimitive.Indicator>
))
NavigationMenuIndicator.displayName =
  NavigationMenuPrimitive.Indicator.displayName

export {
  NavigationMenu, NavigationMenuContent, NavigationMenuIndicator, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, navigationMenuTriggerStyle, NavigationMenuViewport
};

